/**
 * The frontier: roads from a charted atlas place into a draft zone (Season 0, DJ 2026-10-30:
 * "have wickhaven and beyond shown so it looks like there is something further"). A draft is left
 * off the atlas until it is finished, so each such road shows as a short stub fading into a "?"
 * fog bank, unnamed. When the draft is finished and painted onto the atlas, its frontier hint
 * disappears by itself, because its destination is no longer a draft.
 *
 * Which roads: read from the level data (a teleporter on an atlas place whose target zone is a
 * draft). Where: hand-placed below, as an offset from the place's painted anchor in atlas-frame
 * units. `tests/...worldMapFrontier` fails when a draft road has no placement, so a new draft road
 * off the atlas asks for one.
 */
import type { WorldMapLevelLookup, WorldMapZoneLookup } from './worldMapModel';

export interface FrontierPlacement {
  /** Offset of the fog bank from the place's painted anchor, in atlas-frame units (1600x1000). */
  readonly dx: number;
  readonly dy: number;
  /** The road's own name, from the portal ("Highlands Road"); the zone itself stays unnamed. */
  readonly road: string;
}

/** Keyed `${placeId}/${portalId}`. */
export const WORLD_MAP_FRONTIER: Readonly<Record<string, FrontierPlacement>> = {
  'wickhaven/tp_windswept_moor': { dx: 115, dy: -135, road: 'Highlands Road' },
  'wickhaven/tp_goldengrass_savanna': { dx: -205, dy: 170, road: 'Lowlands Road' },
};

export interface FrontierRoad {
  readonly key: string;
  readonly from: string;
  readonly to: string;
  readonly placement: FrontierPlacement | null;
}

interface FrontierPortal { id: string; type?: string; targetMapId?: string }

/** Every road from one of `placeIds` into a draft zone, with its placement when it has one. */
export function frontierRoads(
  placeIds: readonly string[],
  getLevel: WorldMapLevelLookup,
  getZone: WorldMapZoneLookup,
): FrontierRoad[] {
  const roads: FrontierRoad[] = [];
  for (const from of placeIds) {
    const platforms = (getLevel(from)?.platforms ?? []) as readonly FrontierPortal[];
    for (const portal of platforms) {
      const to = portal.targetMapId;
      if (portal.type !== 'teleporter' || !to || getZone(to)?.draft !== true) continue;
      const key = `${from}/${portal.id}`;
      roads.push({ key, from, to, placement: WORLD_MAP_FRONTIER[key] ?? null });
    }
  }
  return roads;
}
